"use client";

import React, { memo } from "react";
import Link from "next/link";
import { ArrowRight, Calendar, Mail } from "lucide-react";
import { trackButtonClick } from "@utils/ga4Tracking";
import webinarCTAData from "@data/components/webinar-cta.json";

interface WebinarCTABoxProps {
  className?: string;
}

interface WebinarCTAData {
  title: string;
  description: string;
  primaryButton: {
    text: string;
    href: string;
  };
  secondaryButton: {
    text: string;
    href: string;
  };
  benefits: string[];
}

const WebinarCTABox = memo<WebinarCTABoxProps>(({ className = "" }) => {
  const data = webinarCTAData as WebinarCTAData;

  const handlePrimaryClick = () => {
    trackButtonClick("webinar_cta_primary", "blog_article", "link");
  };

  const handleSecondaryClick = () => {
    trackButtonClick("webinar_cta_secondary", "blog_article", "link");
  };

  return (
    <div className={`relative my-12 ${className}`}>
      {/* Background with gradient and glass effect */}
      <div className="relative bg-gradient-to-br from-abis-primary via-abis-primary to-abis-primary/90 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20 shadow-2xl overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-abis-accent/20 via-transparent to-abis-accent/20 animate-gradient-shift"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(251,146,60,0.1),transparent_50%)]"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(251,146,60,0.08),transparent_50%)]"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h3
              className="font-bold text-white mb-4 text-2xl md:text-3xl"
              style={{
                fontFamily: "var(--font-kanit, Kanit, sans-serif)",
              }}
            >
              {data.title}
            </h3>
            <p className="text-white/90 text-lg max-w-2xl mx-auto mb-8">
              {data.description}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              href={data.primaryButton.href}
              onClick={handlePrimaryClick}
              className="group relative btn btn-hero h-auto text-lg py-4 px-8 rounded-xl transition-all duration-300 transform shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-abis-accent/50 focus:ring-offset-2 focus:ring-offset-abis-primary overflow-hidden"
              aria-label={data.primaryButton.text}
              title={data.primaryButton.text}
            >
              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-abis-accent/20 to-abis-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <span className="relative flex items-center justify-center">
                <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                {data.primaryButton.text}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Link>

            <Link
              href={data.secondaryButton.href}
              onClick={handleSecondaryClick}
              className="group relative btn btn-hero h-auto !bg-white hover:!bg-white/90 !text-abis-primary text-lg py-4 px-8 rounded-xl transition-all duration-300 transform shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-abis-accent/50 focus:ring-offset-2 focus:ring-offset-abis-primary overflow-hidden"
              aria-label={`${data.secondaryButton.text} - Newsletter abonnieren`}
              title={data.secondaryButton.text}
            >
              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-abis-primary/5 to-abis-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <span className="relative flex items-center justify-center">
                <Mail className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                {data.secondaryButton.text}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Link>
          </div>

          {/* Benefits/Trust indicators */}
          {data.benefits && data.benefits.length > 0 && (
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-white/70 text-sm">
              {data.benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-abis-accent rounded-full"></div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Decorative Elements */}
        <div
          className="absolute top-0 right-0 w-32 h-32 bg-abis-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"
          aria-hidden="true"
        ></div>
        <div
          className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2"
          aria-hidden="true"
        ></div>
      </div>
    </div>
  );
});

WebinarCTABox.displayName = "WebinarCTABox";

export default WebinarCTABox;
